import type { WealthicaAccount } from '@/providers/wealthica2/types';
import { storeLogger, EVENTS } from './logging';

type UnknownRecord = Record<string, unknown>;

export const createAccountStateActions = (set: unknown, get: unknown) => {
  const _set = set as (updater: (state: { connections: Map<string, unknown> }) => Partial<{ connections: Map<string, unknown> }>) => void;
  const _get = get as () => { connections: Map<string, unknown>; fetchPositionsForInstitution: (id: string) => Promise<void> };
  return ({
  // === ACCOUNT MANAGEMENT ===
  setAccountsForInstitution: (institutionId: string, accounts: WealthicaAccount[]) => {
    const debugId = `acc-set-${Date.now()}-${institutionId.slice(-6)}`;

    storeLogger.info('multiWealthica2Store', EVENTS.ACCOUNTS_SET, {
      phase: 'start',
      debugId,
      institutionId,
      accountCount: accounts.length,
      timestamp: new Date().toISOString()
    });

    // Normalize accounts so UI grouping and DB connections match the authoritative institutionId
    const normalizedAccounts: WealthicaAccount[] = (accounts || []).map((acc: WealthicaAccount) => {
      const accRec = acc as unknown as UnknownRecord;
      const instRaw = accRec['institution'] as UnknownRecord | undefined;
      const instObj = instRaw && typeof instRaw === 'object'
        ? instRaw
        : { id: institutionId, name: (accRec['institutionName'] as string) || (instRaw?.['name'] as string) || 'Institution' };
      return {
        ...acc,
        institutionId, // authoritative ID from DB/store
        institution: {
          ...(instObj as UnknownRecord),
          id: institutionId
        }
      } as WealthicaAccount;
    });

    _set((state: { connections: Map<string, unknown> }) => {
      const connection = state.connections.get(institutionId) as UnknownRecord | undefined;
      if (!connection) {
        storeLogger.warn('multiWealthica2Store', EVENTS.ACCOUNTS_SET, {
          phase: 'missing_connection',
          debugId,
          institutionId,
          accountCount: normalizedAccounts.length,
          timestamp: new Date().toISOString()
        });
        return state;
      }

      const newConnections = new Map(state.connections);
      const updatedConnection = {
        ...(connection as UnknownRecord),
        accounts: normalizedAccounts,
        lastAccountsFetch: new Date(),
        isLoadingAccounts: false,
        accountsError: null
      };
      // If another connection shares the same stable identifier but a different rotating ID,
      try {
        const connRec = connection as UnknownRecord;
        const cData = connRec['connectionData'] as UnknownRecord | undefined;
        const stableId = cData ? (cData['institutionIdentifier'] as string | undefined) : undefined;
        if (stableId) {
          for (const [otherId, otherConn] of newConnections.entries()) {
            if (otherId === institutionId) continue;
            const otherRec = otherConn as UnknownRecord;
            const otherData = otherRec['connectionData'] as UnknownRecord | undefined;
            const otherStable = otherData ? (otherData['institutionIdentifier'] as string | undefined) : undefined;
            if (otherStable && otherStable === stableId) {
              const otherAccounts = (otherConn as UnknownRecord)?.accounts as unknown[] | undefined;
              if (otherAccounts && otherAccounts.length > 0) {
                storeLogger.info('multiWealthica2Store', EVENTS.ACCOUNTS_SET, {
                  phase: 'clear_old_rotating',
                  stableIdentifier: stableId,
                  newId: institutionId,
                  oldId: otherId,
                  clearedCount: otherAccounts.length
                });
              }
              // Attempt to migrate caches from old rotating ID to the new one for a seamless UX
              try {
                import('../../providers/wealthica2/utils/cache-management').then(({ rekeyInstitutionCaches }) => {
                  rekeyInstitutionCaches(otherId, institutionId);
                  storeLogger.info('multiWealthica2Store', EVENTS.CACHE_REKEY, {
                    stableIdentifier: stableId,
                    from: otherId,
                    to: institutionId
                  });
                }).catch(() => {});
              } catch { /* noop */ }
              const lastFetch = (otherConn as UnknownRecord)?.lastAccountsFetch;
              newConnections.set(otherId, {
                ...(otherConn as UnknownRecord),
                accounts: [],
                lastAccountsFetch: lastFetch,
                isLoadingAccounts: false,
                accountsError: null
              });
            }
          }
        }
      } catch (rekeyErr) {
        storeLogger.warn('multiWealthica2Store', EVENTS.CACHE_REKEY, { error: rekeyErr, institutionId });
      }
      newConnections.set(institutionId, updatedConnection);

      const connStatus = (connection as UnknownRecord)['status'] as string | undefined;
      const isConnected = (connection as UnknownRecord)['isConnected'] as boolean | undefined;
      storeLogger.info('multiWealthica2Store', EVENTS.ACCOUNTS_SET, {
        phase: 'complete',
        debugId,
        institutionId,
        totalConnections: newConnections.size,
        connectionStatus: connStatus,
        isConnected,
        balanceTotal: normalizedAccounts.reduce((sum, acc) => {
          const bal = Number((acc as unknown as { balance?: number | string }).balance ?? 0);
          return sum + (isNaN(bal) ? 0 : bal);
        }, 0),
        timestamp: new Date().toISOString()
      });

      return { connections: newConnections };
    });

    // CRITICAL FIX: Cache accounts for this institution to create per-institution storage keys
    if (normalizedAccounts.length > 0) {
      import('../../providers/wealthica2/utils/cache-management').then(({ cacheAccountsForInstitution }) => {
        cacheAccountsForInstitution(institutionId, normalizedAccounts as unknown as WealthicaAccount[]);
        storeLogger.info('multiWealthica2Store', EVENTS.ACCOUNTS_CACHED, {
          debugId,
          institutionId,
          accountCount: normalizedAccounts.length,
          cacheKey: `wealthica2_accounts_cache:${institutionId}`
        });
      }).catch((error) => {
        storeLogger.warn('multiWealthica2Store', EVENTS.ACCOUNTS_CACHED, {
          phase: 'failed',
          debugId,
          institutionId,
          error
        });
      });
    }

    // UX IMPROVEMENT: Automatically fetch positions after accounts arrive
    // Run async to avoid blocking account state update or causing nested set calls
    setTimeout(() => {
      try {
        const st = _get();
        const conn = st.connections.get(institutionId);
        if (!conn) return;
        const connRec = conn as UnknownRecord;
        const positionsArr = (connRec['positions'] as unknown[]) || [];
        const hasNoPositions = positionsArr.length === 0;
        const isLoadingPositions = !!(connRec['isLoadingPositions'] as boolean | undefined);
        if (hasNoPositions && !isLoadingPositions) {
          const accounts = (connRec['accounts'] as unknown[]) || [];
          const anyPositionsHint = accounts.some((a) => Array.isArray((a as UnknownRecord)['positions'] as unknown[]) && (((a as UnknownRecord)['positions'] as unknown[])?.length || 0) > 0);
          const allCashish = accounts.length > 0 && accounts.every((a) => {
            const aRec = a as UnknownRecord;
            const classification = (aRec['classification'] as UnknownRecord | undefined);
            const cls = String((classification?.['cdn_asset_class'] as string | undefined) ?? '').toLowerCase();
            const t = String((aRec['type'] as string | undefined) ?? '').toLowerCase();
            return cls === 'cash' || t.includes('cash');
          });
          // Only auto-fetch if not confidently cash-only/no holdings
          if (!(allCashish && !anyPositionsHint)) {
            (st as { fetchPositionsForInstitution: (id: string) => Promise<void> }).fetchPositionsForInstitution(institutionId);
          }
        }
      } catch (e) { /* noop */ }
    }, 0);
  },
  setAccountsLoadingForInstitution: (institutionId: string, loading: boolean) => {
    const debugId = `loading-${Date.now()}-${institutionId.slice(-6)}`;
    storeLogger.info('multiWealthica2Store', EVENTS.ACCOUNTS_LOADING_SET, {
      debugId,
      institutionId,
      loading,
      previousLoading: (_get().connections.get(institutionId) as UnknownRecord | undefined)?.['isLoadingAccounts'],
      timestamp: new Date().toISOString()
    });

    _set((state: { connections: Map<string, unknown> }) => {
      const connection = state.connections.get(institutionId) as UnknownRecord | undefined;
      if (!connection) {
        storeLogger.warn('multiWealthica2Store', EVENTS.ACCOUNTS_LOADING_SET, {
          phase: 'missing_connection',
          debugId,
          institutionId,
          loading,
          timestamp: new Date().toISOString()
        });
        return state;
      }

      const newConnections = new Map(state.connections);
      newConnections.set(institutionId, {
        ...(connection as UnknownRecord),
        isLoadingAccounts: loading
      });

      return { connections: newConnections };
    });
  },

  setAccountsErrorForInstitution: (institutionId: string, error: string | null) => {
    const debugId = `acc-error-${Date.now()}-${institutionId.slice(-6)}`;
    storeLogger.info('multiWealthica2Store', EVENTS.ACCOUNTS_ERROR_SET, {
      debugId,
      institutionId,
      error,
      isClearing: !error,
      wasLoading: (_get().connections.get(institutionId) as UnknownRecord | undefined)?.['isLoadingAccounts'],
      timestamp: new Date().toISOString()
    });

    _set((state: { connections: Map<string, unknown> }) => {
      const connection = state.connections.get(institutionId) as UnknownRecord | undefined;
      if (!connection) {
        storeLogger.warn('multiWealthica2Store', EVENTS.ACCOUNTS_ERROR_SET, {
          phase: 'missing_connection',
          debugId,
          institutionId,
          error,
          timestamp: new Date().toISOString()
        });
        return state;
      }

      const newConnections = new Map(state.connections);
      newConnections.set(institutionId, {
        ...(connection as UnknownRecord),
        accountsError: error,
        isLoadingAccounts: false
      });

      return { connections: newConnections };
    });
  },
  });
};
